// Reusable column-configurator dropdown for tables — show/hide + reorder
// per column, with prefs persisted to localStorage. Originally inlined in
// WorkItemsList (#38); extracted in ctsk-061 so DocumentLibrary can adopt
// the same UI for free, and any future configurable table just imports
// these two exports.
//
// Usage:
//
//   const COLUMNS: ColumnDef[] = [...page-specific list...];
//   const { prefs, setPrefs, visibleCols } = useColPrefs('tasko.documents.cols.v1', COLUMNS);
//   <ColumnsMenu columns={COLUMNS} prefs={prefs} onChange={setPrefs} />
//   ...render only visibleCols in your <thead>/<tbody>...

import { useEffect, useMemo, useRef, useState } from 'react';

export interface ColumnDef {
  key: string;
  label: string;
  width?: number;            // CSS px hint; undefined = flex
  defaultVisible: boolean;
  sortable: boolean;
}

export interface ColPrefs {
  order: string[];        // column keys, in display order
  hidden: string[];       // column keys that are hidden
}

function defaultColPrefs(columns: ColumnDef[]): ColPrefs {
  return {
    order: columns.map((c) => c.key),
    hidden: columns.filter((c) => !c.defaultVisible).map((c) => c.key),
  };
}

function loadColPrefs(storeKey: string, columns: ColumnDef[]): ColPrefs {
  if (typeof window === 'undefined') return defaultColPrefs(columns);
  try {
    const raw = window.localStorage.getItem(storeKey);
    if (!raw) return defaultColPrefs(columns);
    const parsed = JSON.parse(raw) as ColPrefs;
    // Heal across schema changes: drop unknown keys, then slot columns
    // the stored prefs don't know about in at their CANONICAL index
    // (their position in the page's COLUMNS array). This used to append
    // new columns at the end — fine while every new column was a
    // right-edge addition, but CTSK-237's watch/star column is defined
    // leftmost, and end-append would strand it rightmost for every
    // returning user with stored prefs (fresh users were unaffected).
    // For users who have manually reordered, the canonical index is an
    // approximation — acceptable: the column is still discoverable and
    // draggable from the Columns menu like any other.
    const known = new Set(columns.map((c) => c.key));
    const order = parsed.order.filter((k) => known.has(k));
    columns.forEach((c, idx) => {
      if (!order.includes(c.key)) {
        order.splice(Math.min(idx, order.length), 0, c.key);
      }
    });
    const hidden = parsed.hidden.filter((k) => known.has(k));
    // CTSK-521 — a column the stored prefs have never seen takes its declared
    // default: a new default-off column must not switch itself on for
    // returning users.
    const storedKeys = new Set(parsed.order);
    columns.forEach((c) => {
      if (!storedKeys.has(c.key) && !c.defaultVisible && !hidden.includes(c.key)) hidden.push(c.key);
    });
    return { order, hidden };
  } catch {
    return defaultColPrefs(columns);
  }
}

function saveColPrefs(storeKey: string, prefs: ColPrefs): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(storeKey, JSON.stringify(prefs));
}

/**
 * Hook bundling the prefs state, the localStorage persistence, and the
 * derived visibleCols array (already ordered per the prefs). Callers
 * render only the cols this returns.
 */
export function useColPrefs(storeKey: string, columns: ColumnDef[]) {
  const [prefs, setPrefs] = useState<ColPrefs>(() => loadColPrefs(storeKey, columns));
  useEffect(() => { saveColPrefs(storeKey, prefs); }, [storeKey, prefs]);

  const visibleCols = useMemo(
    () => prefs.order
      .map((k) => columns.find((c) => c.key === k))
      .filter((c): c is ColumnDef => !!c && !prefs.hidden.includes(c.key)),
    [prefs.order, prefs.hidden, columns],
  );

  function reset() {
    setPrefs(defaultColPrefs(columns));
  }

  return { prefs, setPrefs, visibleCols, reset };
}

/**
 * The dropdown button + menu. Stateless about persistence — the caller
 * owns the prefs and pipes them in via props.
 */
export function ColumnsMenu({
  columns,
  prefs,
  onChange,
  availableKeys,
}: {
  columns: ColumnDef[];
  prefs: ColPrefs;
  onChange: (next: ColPrefs) => void;
  /**
   * CTSK-521 — when set, only these column keys are offered (and counted).
   * The prefs object stays whole, so the same store backs every surface that
   * shows a subset of the columns (the Documents search results).
   */
  availableKeys?: ReadonlySet<string>;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    if (open) document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  // The keys on offer, in display order. Moves swap with the neighbour in THIS
  // list, so a hidden-from-the-menu column never makes a move look like a no-op.
  const offered = prefs.order.filter((k) => !availableKeys || availableKeys.has(k));
  function swapWithNeighbour(key: string, delta: -1 | 1) {
    const at = offered.indexOf(key);
    const other = offered[at + delta];
    if (at < 0 || other === undefined) return;
    const next = [...prefs.order];
    const a = next.indexOf(key);
    const b = next.indexOf(other);
    [next[a], next[b]] = [next[b]!, next[a]!];
    onChange({ ...prefs, order: next });
  }
  function moveUp(key: string) { swapWithNeighbour(key, -1); }
  function moveDown(key: string) { swapWithNeighbour(key, 1); }
  function toggleVisible(key: string, visible: boolean) {
    const hidden = visible ? prefs.hidden.filter((k) => k !== key) : [...prefs.hidden, key];
    onChange({ ...prefs, hidden });
  }
  function resetDefaults() {
    onChange(defaultColPrefs(columns));
  }

  const visibleCount = offered.filter((k) => !prefs.hidden.includes(k)).length;

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        className="btn"
        onClick={() => setOpen((v) => !v)}
        aria-label="Configure columns"
        title="Show / hide / reorder columns"
        style={{ fontSize: 12 }}
      >
        ☷ Columns ({visibleCount})
      </button>
      {open && (
        <div
          role="menu"
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            marginTop: 4,
            minWidth: 240,
            background: 'var(--surface-raised)',
            border: '1px solid var(--border-strong)',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--shadow-md)',
            zIndex: 1500,
            padding: 6,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 6px 8px' }}>
            <strong style={{ fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.06em' }}>
              Columns
            </strong>
            <button
              className="btn btn--ghost"
              onClick={resetDefaults}
              style={{ fontSize: 11, padding: '2px 6px' }}
            >
              Reset
            </button>
          </div>
          {offered.map((key, idx) => {
            const col = columns.find((c) => c.key === key);
            if (!col) return null;
            const visible = !prefs.hidden.includes(key);
            return (
              <div
                key={key}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '4px 6px',
                  borderRadius: 3,
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--surface-sunken)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1, fontSize: 13 }}>
                  <input
                    type="checkbox"
                    checked={visible}
                    onChange={(e) => toggleVisible(key, e.target.checked)}
                  />
                  {col.label}
                </label>
                <button
                  onClick={() => moveUp(key)}
                  disabled={idx === 0}
                  aria-label={`Move ${col.label} up`}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    cursor: idx === 0 ? 'default' : 'pointer',
                    color: idx === 0 ? 'var(--border-strong)' : 'var(--text-muted)',
                    fontSize: 14,
                    padding: '0 4px',
                  }}
                  title="Move up"
                >
                  ↑
                </button>
                <button
                  onClick={() => moveDown(key)}
                  disabled={idx === offered.length - 1}
                  aria-label={`Move ${col.label} down`}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    cursor: idx === offered.length - 1 ? 'default' : 'pointer',
                    color: idx === offered.length - 1 ? 'var(--border-strong)' : 'var(--text-muted)',
                    fontSize: 14,
                    padding: '0 4px',
                  }}
                  title="Move down"
                >
                  ↓
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
